/**
 * DeployWebPlacementDialog Component
 *
 * Dialog for deploying a web placement to GCS.
 * Auto-triggers regular web placement deployment when opened.
 * A/B tests require choosing whether to update the current publication
 * or create a new analytics iteration.
 * Shows deployment progress and result.
 *
 * States:
 * - configuring: A/B publish mode form
 * - deploying: Show progress indicator
 * - success: Show success message with URL
 * - error: Show error message with retry option
 *
 * @module webplacements/components
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Link,
  IconButton,
  Tooltip,
  Button,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import HomeIcon from '@mui/icons-material/Home';
import CloudIcon from '@mui/icons-material/Cloud';
import { useTheme } from '@mui/material/styles';
import { SDialog, WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useCustomDomain } from '@modules/custom-domains';
import { flowVariablesService } from '@modules/firebase-core/services/FlowVariablesService';
import { WebPlacementDeployService } from '@modules/webplacements/services/WebPlacementDeployService';
import type { FunnelVariable } from '@segmently/schema-models/v2';
import { PUBLISH_PRIVACY_REVIEW } from '@test-kit/selectors/variable-privacy';
import { LAUNCH_ACTIVATION } from '@test-kit/selectors/launch-activation';
import { PublishGateFindings, runActivationAction } from '@modules/launch-activation';
import type { ActivationAction, PublishGateFinding } from '@modules/shared-types/src/launchActivation.types';
import { hasBlockingFinding, MISSING_ATTRIBUTION_ID } from '@modules/webplacements/services/publishGate';
import { DEPLOY_DIALOG_TEST_IDS } from '@modules/webplacements/testIds';
import type {
  PrivacyPreflightResult,
  WebABIterationPublishInput,
  WebPlacement,
  WebPlacementDeployResult,
} from '@modules/webplacements/types/webplacements.types';
import { getFunnelUrl, getLocalDevFunnelUrl } from '@modules/webplacements/types/webplacements.types';

type DeployState = 'configuring' | 'checking' | 'review' | 'gate' | 'deploying' | 'success' | 'error';

/** Class-D dialogs the deploy dialog cannot host itself → land on the page that hosts them, highlighted. */
export function dialogFallbackRoute(projectId: string, dialog: 'domainDrawer' | 'addIntegration' | 'createFunnel'): string {
  switch (dialog) {
    case 'domainDrawer': return `/project/${projectId}/integrations?guide=integrations-custom-domain-section`;
    case 'addIntegration': return `/project/${projectId}/integrations?guide=analytics-add-provider`;
    case 'createFunnel': return `/project/${projectId}/onboardings-v2?guide=onboarding-list-create`;
  }
}

const ANALYTICS_PRIVACY_ERROR_CODE = 'analytics_variable_privacy_review_required';

/**
 * One unreviewed variable in the publish privacy review. A/B tests review the
 * variables of EVERY variant funnel, so each entry remembers which
 * funnel/version to save the decision back to (and which variant to label).
 */
interface PrivacyReviewEntry {
  /** `${funnelId}:${versionId}:${variableId}` — decision map key. */
  key: string;
  funnelId: string;
  versionId: string;
  variable: FunnelVariable;
  /** Variant name for A/B review rows; absent on single placements. */
  variantLabel?: string;
}

const WEBPLACEMENT_PUBLISH_URL_ANCHOR = GUIDE_ANCHORS.webplacementPublishUrl;

/**
 * Props for DeployWebPlacementDialog
 */
export interface DeployWebPlacementDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Web placement to deploy */
  webPlacement: WebPlacement | null;
  /** Deploy function */
  onDeploy: (webPlacementId: string, abIteration?: WebABIterationPublishInput) => Promise<WebPlacementDeployResult>;
  /** Whether deployment is in progress (external state) */
  isDeploying?: boolean;
  /** Project ID for loading custom domain (optional - for custom domain URL display) */
  projectId?: string;
  /** "Fix first" and the attribution finding: step back to the configure step with the toggles highlighted (DC-21, AC-026). */
  onBackToConfigure?: () => void;
  /** Class-P/C finding actions (react-router `navigate` from the host); falls back to window.location.assign. */
  onNavigate?: (path: string) => void;
}

/**
 * DeployWebPlacementDialog Component
 */
export const DeployWebPlacementDialog: React.FC<DeployWebPlacementDialogProps> = ({
  open,
  onClose,
  webPlacement,
  onDeploy,
  projectId,
  onBackToConfigure,
  onNavigate,
}) => {
  const theme = useTheme();
  const guideService = useMemo(() => new GuideService(), []);
  // Deploy service is only used for the side-effect-free server preflight.
  // Constructor throws when VITE_BUILDER_API_URL is unset — degrade to "no
  // preflight" instead of crashing the dialog (server gate stays authoritative).
  const deployService = useMemo(() => {
    try {
      return new WebPlacementDeployService();
    } catch (error) {
      console.warn('[DeployWebPlacementDialog] Deploy service unavailable for preflight:', error);
      return null;
    }
  }, []);

  // Check if running on localhost (to show Local Dev URL only in development)
  const isLocalDev = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Load custom domain for project (to display custom domain URL if active)
  const { domain: customDomain } = useCustomDomain(projectId || '');
  const activeCustomDomain = customDomain?.status === 'active' ? customDomain.domain : null;

  // State
  const [deployState, setDeployState] = useState<DeployState>('configuring');
  const [deployResult, setDeployResult] = useState<WebPlacementDeployResult | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  // Analytics privacy review state (single AND A/B placements, analytics enabled)
  const [reviewEntries, setReviewEntries] = useState<PrivacyReviewEntry[]>([]);
  // Publish gate (DC-21): findings from the server + client-owned attribution check.
  const [findings, setFindings] = useState<PublishGateFinding[]>([]);
  /**
   * Latest committed `findings`, read by `startDeploy`'s AM-3 guard.
   *
   * `startDeploy` MUST NOT depend on gate state: it is a dependency of the
   * open/close effect below, whose close branch resets `findings` — a state
   * value in its dep list makes that effect re-run forever on a dialog that is
   * mounted closed (WebPlacementsPage / ArticlesPage / ArticleEditorPage all
   * mount it with `open={false}`). Every caller of `startDeploy` runs after a
   * commit (a click handler, or a `runGate` continuation that only reaches it
   * with a non-blocking verdict), so the ref is always current where it matters.
   */
  const findingsRef = useRef<PublishGateFinding[]>([]);
  const [gateUnavailable, setGateUnavailable] = useState(false);
  const [reviewDecisions, setReviewDecisions] = useState<Record<string, boolean>>({});
  const [selectedReviewKeys, setSelectedReviewKeys] = useState<Set<string>>(new Set());
  const [savingReview, setSavingReview] = useState(false);
  const [abPublishMode, setAbPublishMode] = useState<'update_current' | 'new_iteration'>('update_current');
  const [iterationName, setIterationName] = useState('');
  const [iterationDescription, setIterationDescription] = useState('');
  const [iterationTags, setIterationTags] = useState('');
  const [iterationChangeReason, setIterationChangeReason] = useState('');

  useEffect(() => { findingsRef.current = findings; }, [findings]);

  // Ref to track if deploy was already triggered (prevents double-trigger)
  const deployTriggeredRef = useRef(false);
  const configInitializedForRef = useRef<string | null>(null);

  const buildABIterationInput = useCallback((): WebABIterationPublishInput | undefined => {
    if (!webPlacement || webPlacement.type !== 'ab_test') return undefined;

    if (abPublishMode === 'update_current') {
      return { mode: 'update_current' };
    }

    const tags = iterationTags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    return {
      mode: 'new_iteration',
      ...(iterationName.trim() && { name: iterationName.trim() }),
      ...(iterationDescription.trim() && { description: iterationDescription.trim() }),
      ...(tags.length && { tags }),
      ...(iterationChangeReason.trim() && { changeReason: iterationChangeReason.trim() }),
    };
  }, [abPublishMode, iterationChangeReason, iterationDescription, iterationName, iterationTags, webPlacement]);

  /**
   * SERVER-driven analytics privacy preflight (single AND A/B placements).
   *
   * Delegates to `POST /api/publish/web/healthcheck` via
   * WebPlacementDeployService.preflightPrivacyReview — the SAME evaluation
   * the publish gate runs, so the review list can never diverge from the
   * server's view. Returns true when the dialog entered the review state;
   * false means "nothing to review" OR the check itself was unavailable —
   * the server gate stays authoritative either way (its 422 re-enters here,
   * and an unresolvable 422 degrades to typed copy, never the raw message).
   */
  /**
   * Loads the full variable objects for a server-reported unreviewed list and
   * enters the review state. Split out of `runPrivacyPreflight` (AC-063,
   * AC-065) so both the client preflight AND `runGate`'s privacy branch can
   * enter the SAME review UI without duplicating the variable-loading logic.
   */
  const enterReview = useCallback(async (
    unreviewed: PrivacyPreflightResult['unreviewed']
  ): Promise<boolean> => {
    if (!projectId) return false;

    // Server evidence decides WHICH variables need review; load the full
    // variable objects so decisions can be saved back to the owning funnel.
    const variablesByScope = new Map<string, Record<string, FunnelVariable>>();
    const entries: PrivacyReviewEntry[] = [];
    for (const item of unreviewed) {
      const scopeKey = `${item.funnelId}:${item.versionId}`;
      let variables = variablesByScope.get(scopeKey);
      if (!variables) {
        variables = await flowVariablesService.getVariables(projectId, item.funnelId, item.versionId);
        variablesByScope.set(scopeKey, variables);
      }
      const variable = variables[item.variableId];
      if (!variable) {
        console.warn(
          '[DeployWebPlacementDialog] Server-reported variable not found locally, skipping row:',
          item.variableId
        );
        continue;
      }
      entries.push({
        key: `${item.funnelId}:${item.versionId}:${item.variableId}`,
        funnelId: item.funnelId,
        versionId: item.versionId,
        variable,
        variantLabel: item.variantLabel,
      });
    }
    if (entries.length === 0) return false;

    entries.sort((a, b) =>
      (a.variantLabel ?? '').localeCompare(b.variantLabel ?? '')
      || (a.variable.order ?? 0) - (b.variable.order ?? 0));
    setReviewEntries(entries);
    setReviewDecisions({});
    setSelectedReviewKeys(new Set());
    setDeployState('review');
    return true;
  }, [projectId]);

  const runPrivacyPreflight = useCallback(async (): Promise<boolean> => {
    if (!webPlacement || !projectId || !deployService) return false;

    try {
      const preflight = await deployService.preflightPrivacyReview(projectId, webPlacement);
      if (preflight.status !== 'review_required') return false;
      return await enterReview(preflight.unreviewed);
    } catch (error) {
      console.warn(
        '[DeployWebPlacementDialog] Privacy preflight failed — continuing to publish (server gate still enforces):',
        error
      );
      return false;
    }
  }, [deployService, enterReview, projectId, webPlacement]);

  /**
   * DC-21 publish gate opt-in (IMP-2): the findings block — and the client-only
   * `missing_attribution` finding it renders — only exist for a host that passed
   * `onBackToConfigure`, because that is the only host whose "back to step 2" action
   * can actually land somewhere. WebPlacementsPage / WebABTestsPage / ArticlesPage /
   * ArticleEditorPage stay on the pre-Plan-C privacy-review-only path until Plan D
   * wires them.
   */
  const gateEnabled = Boolean(onBackToConfigure);

  /**
   * DC-21 gate: healthcheck BEFORE the auto-deploy. Only errors pause; warnings ride along
   * to the result.
   *
   * NEVER rejects (AC-065): any failure inside the gate — the healthcheck, or the variable
   * load `enterReview` performs — degrades to "check unavailable, publish proceeds" instead
   * of leaving the dialog wedged in `checking` with no actions. The server gate stays
   * authoritative either way.
   */
  const runGate = useCallback(async (): Promise<'review' | 'gate' | 'proceed'> => {
    // IMP-2 / DC-21 opt-in: only a host that can honour a finding's "back to step 2"
    // action gets the gate. Every other host keeps the exact pre-Plan-C behaviour —
    // the privacy preflight alone, no findings block, no blocking pause.
    if (!gateEnabled) {
      const entered = await runPrivacyPreflight();
      return entered ? 'review' : 'proceed';
    }
    // MIN-5: a gate that never ran must never read as a passed check.
    if (!webPlacement || !projectId || !deployService) {
      setFindings([]);
      setGateUnavailable(true);
      return 'proceed';
    }
    try {
      const result = await deployService.runPublishGate(projectId, webPlacement);
      setFindings(result.findings);
      setGateUnavailable(result.unavailable);
      if (result.privacy.status === 'review_required') {
        const entered = await enterReview(result.privacy.unreviewed);
        if (entered) return 'review';
      }
      if (hasBlockingFinding(result.findings)) {
        setDeployState('gate');
        return 'gate';
      }
      return 'proceed';
    } catch (error) {
      console.warn(
        '[DeployWebPlacementDialog] Publish gate failed — publishing anyway (server gate still enforces):',
        error
      );
      setFindings([]);
      setGateUnavailable(true);
      return 'proceed';
    }
  }, [deployService, enterReview, gateEnabled, projectId, runPrivacyPreflight, webPlacement]);

  const startDeploy = useCallback(() => {
    if (!webPlacement) return;
    // AM-3: a blocking finding pauses the publish no matter which button called us
    // (the gate's own Publish is disabled; the A/B "Publish anyway" escape hatch is
    // only an escape from the PRIVACY review, never from a blocking finding).
    if (hasBlockingFinding(findingsRef.current)) {
      setDeployState('gate');
      return;
    }

    deployTriggeredRef.current = true;
    setDeployResult(null);
    setCopiedUrl(null);
    setDeployState('deploying');

    onDeploy(webPlacement.id, buildABIterationInput())
      .then(async (result) => {
        // Server privacy gate fired anyway (variables changed outside the UI,
        // races) — reload variables and reopen the in-dialog review instead of
        // showing an error.
        if (!result.success && result.errorCode === ANALYTICS_PRIVACY_ERROR_CODE) {
          const enteredReview = await runPrivacyPreflight();
          if (enteredReview) return;
        }
        setDeployResult(result);
        setDeployState(result.success ? 'success' : 'error');
      })
      .catch((error) => {
        setDeployResult({
          success: false,
          webUrl: '',
          gcsFiles: [],
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        setDeployState('error');
      });
  }, [buildABIterationInput, onDeploy, runPrivacyPreflight, webPlacement]);

  /** Persist all review decisions to their owning funnels, then publish (unless a blocking finding is already known). */
  const handleSaveReviewAndPublish = useCallback(async () => {
    if (!webPlacement || !projectId) return;

    setSavingReview(true);
    try {
      for (const entry of reviewEntries) {
        const decision = reviewDecisions[entry.key];
        if (typeof decision !== 'boolean') return;
        await flowVariablesService.saveVariable(projectId, entry.funnelId, entry.versionId, {
          ...entry.variable,
          excludeFromAnalytics: decision,
          updatedAt: new Date(),
        });
      }
      if (hasBlockingFinding(findings)) {
        setDeployState('gate');
      } else {
        startDeploy();
      }
    } catch (error) {
      setDeployResult({
        success: false,
        webUrl: '',
        gcsFiles: [],
        error: error instanceof Error ? error.message : 'Failed to save analytics decisions',
      });
      setDeployState('error');
    } finally {
      setSavingReview(false);
    }
  }, [findings, projectId, reviewDecisions, reviewEntries, startDeploy, webPlacement]);

  const allReviewDecided = reviewEntries.length > 0
    && reviewEntries.every((entry) => typeof reviewDecisions[entry.key] === 'boolean');
  const decidedCount = reviewEntries
    .filter((entry) => typeof reviewDecisions[entry.key] === 'boolean').length;

  // ── Bulk review selection (variables-bulk-actions) ──
  // Pure local reviewDecisions fill for the CHECKED rows; the allReviewDecided
  // gate, save chain, skip button and 422 re-entry stay untouched.
  const allReviewSelected = reviewEntries.length > 0
    && reviewEntries.every((entry) => selectedReviewKeys.has(entry.key));
  const someReviewSelected = selectedReviewKeys.size > 0 && !allReviewSelected;

  const toggleReviewRowSelected = useCallback((key: string) => {
    setSelectedReviewKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleReviewSelectAll = useCallback(() => {
    setSelectedReviewKeys((previous) =>
      previous.size === reviewEntries.length
        ? new Set()
        : new Set(reviewEntries.map((entry) => entry.key)));
  }, [reviewEntries]);

  const applyBulkDecision = useCallback((exclude: boolean) => {
    setReviewDecisions((previous) => ({
      ...previous,
      ...Object.fromEntries([...selectedReviewKeys].map((key) => [key, exclude])),
    }));
  }, [selectedReviewKeys]);

  /** A/B confirm button: run the publish gate on the variant funnels BEFORE publishing. */
  const handleAbPublishClick = useCallback(() => {
    setDeployState('checking');
    runGate()
      .then((verdict) => {
        if (verdict === 'proceed') startDeploy();
      })
      // Belt-and-braces: `runGate` already swallows its own failures, so this only
      // catches a defect in the gate itself — never leave the dialog in `checking`.
      .catch(() => startDeploy());
  }, [runGate, startDeploy]);

  // Auto-trigger deploy for regular placements — after the publish gate
  // (privacy preflight + blocking findings). A/B tests require choosing
  // whether this is a current-publication update or a new iteration.
  useEffect(() => {
    if (open && webPlacement && !deployTriggeredRef.current && webPlacement.type !== 'ab_test') {
      deployTriggeredRef.current = true;
      setDeployState('checking');
      runGate()
        .then((verdict) => {
          if (verdict === 'proceed') startDeploy();
        })
        .catch(() => startDeploy());
    }

    if (
      open &&
      webPlacement?.type === 'ab_test' &&
      !deployTriggeredRef.current &&
      configInitializedForRef.current !== webPlacement.id
    ) {
      configInitializedForRef.current = webPlacement.id;
      setDeployResult(null);
      setCopiedUrl(null);
      setDeployState('configuring');
      setAbPublishMode(webPlacement.abCurrentIteration ? 'update_current' : 'new_iteration');
      const nextSequence = (webPlacement.abIterationSequence || 0) + 1;
      setIterationName(`Iteration ${nextSequence}`);
      setIterationDescription('');
      setIterationTags(webPlacement.tags?.join(', ') || '');
      setIterationChangeReason('');
    }

    // Reset trigger flag when dialog closes
    if (!open) {
      deployTriggeredRef.current = false;
      configInitializedForRef.current = null;
      // Idempotent by construction: a closed dialog must reach a fixed point on the
      // FIRST run of this effect — a fresh `[]`/`{}` identity on every run would keep
      // re-triggering anything that depends on this state.
      setReviewEntries((previous) => (previous.length ? [] : previous));
      setReviewDecisions((previous) => (Object.keys(previous).length ? {} : previous));
      setSavingReview(false);
      setFindings((previous) => (previous.length ? [] : previous));
      setGateUnavailable(false);
    }
  }, [open, runGate, startDeploy, webPlacement]);

  // Compute preview URL - uses custom domain if active (Cloud URL)
  const cloudUrl = webPlacement ? getFunnelUrl(webPlacement.alias, activeCustomDomain) : '';

  // Compute local dev URL with Debug Panel
  const localDevUrl = webPlacement ? getLocalDevFunnelUrl(webPlacement.alias) : '';

  /**
   * Copy URL to clipboard
   */
  const handleCopyUrl = useCallback(async (url: string) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  }, []);

  /**
   * Handle retry - reset and re-trigger deploy
   */
  const handleRetry = useCallback(() => {
    deployTriggeredRef.current = false;
    setDeployResult(null);
    setDeployState('deploying');

    // Re-trigger deploy
    startDeploy();
  }, [startDeploy]);

  /**
   * A finding's action button (DC-21): the attribution finding and any
   * dialog-less class-D action always go back to step 2 (this dialog cannot
   * host that toggle); everything else dispatches through the shared
   * launch-activation router, closing this dialog first since P/C/D-with-dialog
   * all leave it behind.
   */
  const handleFindingAction = useCallback((finding: PublishGateFinding, action: ActivationAction) => {
    if (finding.id === MISSING_ATTRIBUTION_ID || (action.class === 'D' && !action.dialog)) {
      onBackToConfigure?.();
      return;
    }
    const go = (path: string) => { onClose(); (onNavigate ?? ((p: string) => window.location.assign(p)))(path); };
    runActivationAction(action, {
      onNavigate: go,
      openDialog: (dialog) => go(dialogFallbackRoute(projectId ?? '', dialog)),
    });
  }, [onBackToConfigure, onClose, onNavigate, projectId]);

  // Don't render if no web placement
  if (!webPlacement) {
    return null;
  }

  // Publish gate findings block (DC-21, AC-027): rendered in the `gate` state alone, and
  // alongside the result once the check has run (review divider, success — the clean row
  // only ever shows next to an actual published result; error only shows real findings).
  const gateBlock = gateEnabled && (findings.length > 0 || gateUnavailable || deployState === 'success') && (
    <PublishGateFindings findings={findings} unavailable={gateUnavailable} onAction={handleFindingAction} />
  );

  return (
    <SDialog
      open={open}
      onClose={onClose}
      data-testid={DEPLOY_DIALOG_TEST_IDS.dialog}
      title={
        deployState === 'success'
          ? 'Published Successfully!'
          : deployState === 'error'
            ? 'Deployment Failed'
            : deployState === 'configuring'
              ? 'Publish A/B Test'
              : deployState === 'gate'
                ? 'Fix before publishing'
                : deployState === 'review'
                  ? 'Review analytics collection'
                  : deployState === 'checking'
                    ? 'Preparing...'
                    : 'Publishing...'
      }
      maxWidth="sm"
      fullWidth
      primaryAction={
        deployState === 'error'
          ? {
              label: 'Retry',
              onClick: handleRetry,
              'data-testid': DEPLOY_DIALOG_TEST_IDS.retryBtn,
            }
          : deployState === 'success'
            ? {
                label: 'Done',
                onClick: onClose,
                'data-testid': DEPLOY_DIALOG_TEST_IDS.doneBtn,
              }
            : deployState === 'gate'
              ? {
                  label: 'Publish',
                  onClick: startDeploy,
                  disabled: true,
                  'data-testid': LAUNCH_ACTIVATION.gatePublish,
                }
              : deployState === 'review'
                ? {
                    label: 'Save & Publish',
                    onClick: handleSaveReviewAndPublish,
                    disabled: !allReviewDecided,
                    loading: savingReview,
                    'data-testid': PUBLISH_PRIVACY_REVIEW.saveButton,
                  }
                : undefined
      }
      secondaryAction={
        deployState === 'gate'
          ? {
              label: 'Fix first',
              onClick: onBackToConfigure ?? onClose,
              'data-testid': LAUNCH_ACTIVATION.gateFixFirst,
            }
          // A/B escape hatch: the server privacy gate only fires on
          // POST /api/publish/web (single/variant SSG publishes) — an A/B
          // routing publish is NOT server-gated, so the client review must not
          // be a harder gate than the server's. Variants get the blocking
          // review at their own publish time. Single placements keep the
          // blocking review — skipping would only round-trip through the 422.
          : deployState === 'review' && webPlacement?.type === 'ab_test'
            ? {
                label: 'Publish anyway',
                onClick: startDeploy,
                disabled: savingReview || hasBlockingFinding(findings),
                'data-testid': PUBLISH_PRIVACY_REVIEW.skipButton,
              }
            : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
          minHeight: 200,
        }}
      >
        {/* A/B publish mode */}
        {deployState === 'configuring' && webPlacement.type === 'ab_test' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <Alert severity="info">
              Choose whether this publish updates the current A/B publication or creates a new analytics iteration.
            </Alert>

            <RadioGroup
              value={abPublishMode}
              onChange={(event) => setAbPublishMode(event.target.value as 'update_current' | 'new_iteration')}
            >
              <FormControlLabel
                value="update_current"
                control={<Radio />}
                label="Update current publication"
                data-testid="webabt-publish-mode-update-current"
              />
              <FormControlLabel
                value="new_iteration"
                control={<Radio />}
                label="Publish new iteration"
                data-testid="webabt-publish-mode-new-iteration"
              />
            </RadioGroup>

            {abPublishMode === 'new_iteration' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                <TextField
                  label="Iteration Name"
                  value={iterationName}
                  onChange={(event) => setIterationName(event.target.value)}
                  data-testid="webabt-publish-iteration-name"
                  fullWidth
                />
                <TextField
                  label="Iteration Description"
                  value={iterationDescription}
                  onChange={(event) => setIterationDescription(event.target.value)}
                  data-testid="webabt-publish-iteration-description"
                  fullWidth
                  multiline
                  rows={3}
                />
                <TextField
                  label="Iteration Tags"
                  value={iterationTags}
                  onChange={(event) => setIterationTags(event.target.value)}
                  helperText="Comma-separated tags"
                  data-testid="webabt-publish-iteration-tags"
                  fullWidth
                />
                <TextField
                  label="Change Reason"
                  value={iterationChangeReason}
                  onChange={(event) => setIterationChangeReason(event.target.value)}
                  data-testid="webabt-publish-iteration-change-reason"
                  fullWidth
                  multiline
                  rows={2}
                />
              </Box>
            )}

            <Button
              variant="contained"
              onClick={handleAbPublishClick}
              data-testid="webabt-publish-confirm"
            >
              {abPublishMode === 'new_iteration' ? 'Publish New Iteration' : 'Update Current Publication'}
            </Button>
          </Box>
        )}

        {/* Checking State — analytics privacy preflight before publish */}
        {deployState === 'checking' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: theme.spacing(4),
              gap: theme.spacing(2),
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Checking analytics settings...
            </Typography>
          </Box>
        )}

        {/* Publish Gate — a blocking finding pauses here (DC-21, AM-3) */}
        {deployState === 'gate' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
            <Alert severity="error">
              A blocking finding must be fixed before this placement can be published.
            </Alert>
            {gateBlock}
          </Box>
        )}

        {/* Analytics Privacy Review State */}
        {deployState === 'review' && (
          <Box
            data-testid={PUBLISH_PRIVACY_REVIEW.container}
            sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}
          >
            <Alert severity="info" sx={{ mb: theme.spacing(1) }}>
              Analytics is enabled for this project, so every funnel variable needs an
              explicit collection decision before publishing. Routing and personalization
              keep working either way — this only controls what reaches analytics.
            </Alert>

            <Typography variant="caption" color="text.secondary">
              {decidedCount} of {reviewEntries.length} decided
            </Typography>

            {/* Bulk bar — fills reviewDecisions for the checked rows only */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                flexWrap: 'wrap',
                p: theme.spacing(0.5, 1),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: theme.palette.action.hover,
              }}
            >
              <Checkbox
                data-testid={PUBLISH_PRIVACY_REVIEW.selectAll}
                size="small"
                checked={allReviewSelected}
                indeterminate={someReviewSelected}
                onChange={toggleReviewSelectAll}
                disabled={savingReview}
              />
              <Typography
                data-testid={PUBLISH_PRIVACY_REVIEW.selectionCount}
                variant="caption"
                sx={{ minWidth: 72 }}
              >
                {selectedReviewKeys.size} selected
              </Typography>
              <Button
                data-testid={PUBLISH_PRIVACY_REVIEW.bulkCollect}
                size="small"
                variant="outlined"
                onClick={() => applyBulkDecision(false)}
                disabled={selectedReviewKeys.size === 0 || savingReview}
                sx={{ textTransform: 'none' }}
              >
                Collect selected
              </Button>
              <Button
                data-testid={PUBLISH_PRIVACY_REVIEW.bulkExclude}
                size="small"
                variant="outlined"
                onClick={() => applyBulkDecision(true)}
                disabled={selectedReviewKeys.size === 0 || savingReview}
                sx={{ textTransform: 'none' }}
              >
                Don't collect selected
              </Button>
              <Typography variant="caption" color="text.secondary">
                applies to checked rows · rows stay editable below
              </Typography>
            </Box>

            {reviewEntries.map((entry) => {
              const { variable } = entry;
              const decision = reviewDecisions[entry.key];
              return (
                <Box
                  key={entry.key}
                  data-testid={PUBLISH_PRIVACY_REVIEW.row(variable.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                    p: theme.spacing(1.5),
                    border: `1px solid ${typeof decision === 'boolean' ? theme.palette.success.light : theme.palette.divider}`,
                    borderRadius: 1,
                    ...(selectedReviewKeys.has(entry.key) && {
                      bgcolor: theme.palette.action.selected,
                    }),
                  }}
                >
                  <Checkbox
                    data-testid={PUBLISH_PRIVACY_REVIEW.rowCheckbox(variable.id)}
                    size="small"
                    checked={selectedReviewKeys.has(entry.key)}
                    onChange={() => toggleReviewRowSelected(entry.key)}
                    disabled={savingReview}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }} noWrap>
                    {variable.name}
                  </Typography>
                  {entry.variantLabel && (
                    <Chip
                      label={entry.variantLabel}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem' }}
                    />
                  )}
                  <Chip label={variable.type} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={decision === false ? 'collect' : decision === true ? 'exclude' : null}
                    onChange={(_event, value: 'collect' | 'exclude' | null) => {
                      if (!value) return;
                      setReviewDecisions((previous) => ({
                        ...previous,
                        [entry.key]: value === 'exclude',
                      }));
                    }}
                    disabled={savingReview}
                  >
                    <ToggleButton
                      value="collect"
                      data-testid={PUBLISH_PRIVACY_REVIEW.collect(variable.id)}
                      sx={{ textTransform: 'none', px: theme.spacing(1.5) }}
                    >
                      Collect
                    </ToggleButton>
                    <ToggleButton
                      value="exclude"
                      data-testid={PUBLISH_PRIVACY_REVIEW.exclude(variable.id)}
                      sx={{ textTransform: 'none', px: theme.spacing(1.5) }}
                    >
                      Don't collect
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              );
            })}

            <Typography variant="caption" color="text.secondary" sx={{ mt: theme.spacing(0.5) }}>
              Decisions are saved to the funnel variables and can be changed later in the
              placement's Variables section.
              {webPlacement.type === 'ab_test' && (
                <> "Publish anyway" updates only the A/B traffic routing — each variant funnel
                will still require these decisions the next time it is published itself.</>
              )}
            </Typography>

            {gateBlock && (
              <>
                <Divider sx={{ my: 1 }} />
                {gateBlock}
              </>
            )}
          </Box>
        )}

        {/* Deploying State */}
        {deployState === 'deploying' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: theme.spacing(4),
              gap: theme.spacing(2),
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              Publishing web funnel...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Generating static pages and uploading to CDN
            </Typography>
          </Box>
        )}

        {/* Success State */}
        {deployState === 'success' && deployResult && (
          <>
            <Alert severity="success" sx={{ mb: theme.spacing(2) }}>
              Your web funnel is now live!
            </Alert>

            {/* Local Dev URL (with Debug Panel) - only shown on localhost */}
            {isLocalDev && (
              <Box
                sx={{
                  p: theme.spacing(2),
                  bgcolor: theme.palette.primary.main + '10',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <HomeIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Local Dev (with Debug Panel)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Link
                    href={localDevUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500, flexGrow: 1 }}
                  >
                    {localDevUrl}
                  </Link>
                  <Tooltip title={copiedUrl === localDevUrl ? 'Copied!' : 'Copy URL'}>
                    <IconButton size="small" onClick={() => handleCopyUrl(localDevUrl)}>
                      {copiedUrl === localDevUrl ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Cloud URL */}
            <Box
              sx={{
                p: theme.spacing(2),
                bgcolor: theme.palette.success.main + '10',
                borderRadius: 1,
                border: `1px solid ${theme.palette.success.main}30`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <CloudIcon sx={{ fontSize: 16, color: 'info.main' }} />
                <WithGuideHelp
                  guideKey={WEBPLACEMENT_PUBLISH_URL_ANCHOR.guideKey}
                  guideService={guideService}
                  data-testid={WEBPLACEMENT_PUBLISH_URL_ANCHOR.testId}
                  position="inline-end"
                >
                  <Typography variant="caption" color="text.secondary">
                    Cloud URL
                  </Typography>
                </WithGuideHelp>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link
                  href={cloudUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={DEPLOY_DIALOG_TEST_IDS.publishedUrlLink}
                  sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500, flexGrow: 1 }}
                >
                  {cloudUrl}
                </Link>
                <Tooltip title={copiedUrl === cloudUrl ? 'Copied!' : 'Copy URL'}>
                  <IconButton size="small" onClick={() => handleCopyUrl(cloudUrl)} data-testid={DEPLOY_DIALOG_TEST_IDS.copyUrlBtn}>
                    {copiedUrl === cloudUrl ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {gateBlock && (
              <>
                <Divider sx={{ my: 1 }} />
                {gateBlock}
              </>
            )}
          </>
        )}

        {/* Error State */}
        {deployState === 'error' && deployResult && (
          <>
            {/* The privacy gate never surfaces the raw server sentence — the
                review flow handles it; this copy is the last-resort fallback
                when the review list itself could not be built. */}
            {deployResult.errorCode === ANALYTICS_PRIVACY_ERROR_CODE ? (
              <Alert severity="warning" sx={{ mb: theme.spacing(2) }}>
                Some funnel variables still need an analytics privacy review
                before this funnel can be published with analytics enabled.
                Open the placement's Variables section and choose "Collect" or
                "Don't collect" for each variable marked "Review required",
                then publish again.
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
                {deployResult.error || 'Deployment failed. Please try again.'}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              If the problem persists, please check:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: theme.spacing(2) }}>
              <Typography component="li" variant="body2" color="text.secondary">
                The onboarding has at least one screen
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                A launch screen is set for the version
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Your network connection is stable
              </Typography>
            </Box>

            {gateBlock && (
              <>
                <Divider sx={{ my: 1 }} />
                {gateBlock}
              </>
            )}
          </>
        )}
      </Box>
    </SDialog>
  );
};

export default DeployWebPlacementDialog;
